import { NavigateBefore, NavigateNext } from '@mui/icons-material';
import { Box, IconButton, SxProps } from '@mui/material';
import { first, last } from 'lodash';
import moment from 'moment';
import * as React from 'react';
import { useCallback, useMemo } from 'react';

import { EmployeeCalendarDate } from '~/components/EmployeeCalendar';
import {
  CELL_SPACING,
  CELL_WIDTH,
  DAY_GRAY,
  EmployeeCalendarMonth,
  WEEKEND_GRAY,
} from '~/components/EmployeeCalendar/data';
import { TooltippedTruncatedText } from '~/components/TooltippedTruncatedText';
import { mergeSxProps } from '~/utils/helpers/sxProps';

import { useSxStyles } from './useStyles';

export type EmployeeCalendarNavigation = {
  enabled: {
    next: boolean;
    previous: boolean;
  };
  onNavigate: (direction: string) => void;
};

export type PropsType = {
  datesInView: EmployeeCalendarDate[];
  monthsInView: EmployeeCalendarMonth[];
  showMonth?: boolean;
  hideOutOfRange?: boolean;
  navigation: EmployeeCalendarNavigation;
};

const EmployeeCalendarHeaderMonth = (props: {
  month: EmployeeCalendarMonth;
}) => {
  const {
    month: { daysCount, label },
  } = props;

  const sxStyles = useSxStyles();

  const monthWidth = useMemo(() => {
    const cellsWidth = CELL_WIDTH * daysCount;
    const paddingsWidth = CELL_SPACING * (daysCount - 1) * 2;

    return `${cellsWidth + paddingsWidth}px`;
  }, [daysCount]);

  return (
    <Box sx={mergeSxProps(sxStyles.dayCellMonth, { width: monthWidth })}>
      {label}
    </Box>
  );
};

export const EmployeeCalendarHeader: React.FC<PropsType> = (
  props: PropsType,
) => {
  const {
    datesInView,
    monthsInView,
    showMonth,
    hideOutOfRange,
    navigation: { enabled, onNavigate },
  } = props;

  const sxStyles = useSxStyles();

  const { shownRange } = useMemo(() => {
    const shownDates = datesInView.filter((d) => d.inRange);

    const getRangeDate = (date?: Date): string =>
      !!date ? moment(date).format('L') : '';

    return {
      shownRange: {
        start: getRangeDate(first(shownDates)?.date),
        end: getRangeDate(last(shownDates)?.date),
      },
    };
  }, [datesInView]);

  const getMonthDetails = useCallback(
    (day: Date): EmployeeCalendarMonth | undefined => {
      return monthsInView.find((m) =>
        moment(m.showOnDate).isSame(moment(day), 'day'),
      );
    },
    [monthsInView],
  );

  const getDayStyles = useCallback(
    (calDate: EmployeeCalendarDate) => {
      const { isWeekend, isToday, inRange } = calDate;

      const weekDayStyle = {
        backgroundColor: isWeekend ? WEEKEND_GRAY : DAY_GRAY,
      };

      const isOutOfRangeStyle = (sx: SxProps) => {
        return !inRange
          ? hideOutOfRange
            ? sxStyles.hideOutOfRange
            : sx
          : undefined;
      };

      return {
        wrapper: mergeSxProps(
          sxStyles.dayCellWrapper,
          isToday ? sxStyles.dayToday : undefined,
          isOutOfRangeStyle(sxStyles.endOfRangeWrapper),
        ),
        cell: mergeSxProps(
          sxStyles.dayCell,
          weekDayStyle,
          isOutOfRangeStyle(sxStyles.endOfRange),
        ),
      };
    },
    [sxStyles, hideOutOfRange],
  );

  if (!datesInView.length) return <div />;

  return (
    <Box>
      <>
        <Box
          sx={{
            ...sxStyles.navigationWrapper,
            width: datesInView.length * (CELL_WIDTH + CELL_SPACING * 2),
          }}
        >
          <IconButton
            size="medium"
            onClick={() => onNavigate('LEFT')}
            disabled={!enabled.previous}
          >
            <NavigateBefore fontSize="inherit" />
          </IconButton>

          <Box sx={sxStyles.startEndDates}>
            <TooltippedTruncatedText>
              {shownRange.start}
            </TooltippedTruncatedText>
            {'\xa0-\xa0'}
            <TooltippedTruncatedText>{shownRange.end}</TooltippedTruncatedText>
          </Box>

          <IconButton
            size="medium"
            onClick={() => onNavigate('RIGHT')}
            disabled={!enabled.next}
          >
            <NavigateNext fontSize="inherit" />
          </IconButton>
        </Box>
        <Box sx={mergeSxProps(sxStyles.dayCells)}>
          {datesInView.map((calDate) => {
            const { id, date, day, weekday } = calDate;

            const cellStyles = getDayStyles(calDate);
            const monthDetails = showMonth ? getMonthDetails(date) : undefined;

            return (
              <Box
                key={id}
                sx={mergeSxProps(
                  cellStyles.wrapper,
                  showMonth ? sxStyles.dayCellWrapperWithMonth : undefined,
                )}
              >
                {!!monthDetails && (
                  <EmployeeCalendarHeaderMonth month={monthDetails} />
                )}
                <Box sx={cellStyles.cell}>
                  <Box>{weekday}</Box>
                  <Box>{day}</Box>
                </Box>
              </Box>
            );
          })}
        </Box>
      </>
    </Box>
  );
};
